/**
 * @fileoverview Parent component that controls Click&Pledge visibility and initialization.
 * Determines whether to show the Click&Pledge interface based on authentication and settings.
 */

import React from 'react';

import { formatTimestampToISO, generateCustomerId } from '../../../base/environment/utils';
import { getLocalParticipant } from '../../../base/participants';
import { connect } from '../../../base/redux';
import { isDevelopment } from '../../../base/util';

import ClickAndPledge from './clickAndPledge';

/**
 * ClickandPledge parent component that decides whether to show the donation interface.
 * Constructs meeting data and conditionally renders the main ClickAndPledge component
 * based on authentication token and enabled status.
 *
 * @param {Object} props - Component props
 * @param {boolean} props._isClickandPledgeEnabled - Whether Click&Pledge is enabled
 * @param {string} props.giventToken - Authentication token for Givent service
 * @param {string} props.moderatorEmail - Email of the meeting moderator
 * @param {string} props.room - Meeting room identifier
 * @param {string} props.name - Name of the local participant
 * @param {string} props.meetingTime - Meeting start time
 * @returns {ReactElement|null} ClickAndPledge component or null if not enabled
 */
function ClickandPledgeParent({ _isClickandPledgeEnabled, giventToken, moderatorEmail, room, name, userID, meetingTime, formGUID }) {
    const giventUserId = userID ? `meethourUser_${userID}` : generateCustomerId();
    const APPID = '3580'; // Static App ID from Click&Pledge HostAccountID

    const meetHourFinalData = {
        GiventToken: giventToken,
        HostAcctID: isDevelopment() ? '7k-0Rwy-SWaPnxGuDwPL1Q' : `meethourAppID_${APPID}`,
        HostContactName: name,
        HostEmail: moderatorEmail,
        HostID: giventUserId,
        HostName: name,
        HostUserID: giventUserId,
        MeetingID: room,
        MeetingStartTime: meetingTime,
        Platform: 'meethour'
    };

    return (
        (giventToken || formGUID) && _isClickandPledgeEnabled && <ClickAndPledge meetHourFinalData = { meetHourFinalData } />
    );
}

/**
 * Maps parts of the Redux state to the component's props.
 * Extracts Click&Pledge settings, user data, and meeting information.
 *
 * @param {Object} state - The Redux state
 * @private
 * @returns {Object} Object containing mapped state properties
 */
function _mapStateToProps(state) {
    const { isEnabled, formGUID, giventToken } = state['features/clickandpledge'];
    const localParticipant = getLocalParticipant(state);
    const { room, conferenceTimestamp } = state['features/base/conference'];
    const meetingTime = conferenceTimestamp === undefined ? undefined : formatTimestampToISO(conferenceTimestamp);
    const userID = state['features/base/mtApi']?.userDetails?.id;

    return {
        _isClickandPledgeEnabled: isEnabled,
        giventToken,
        formGUID,
        moderatorEmail: localParticipant?.email,
        room,
        name: localParticipant?.name,
        meetingTime,
        userID
    };
}

export default connect(_mapStateToProps, null)(ClickandPledgeParent);
